import first from "lodash/first";
import React, { useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { newsletter as newsletterData } from "shared/data/newsletter";
import { notEmpty } from "shared/lib/utils";

import type { newsletterDetailsNewsletter$key } from "scmp-app/queries/__generated__/newsletterDetailsNewsletter.graphql";

import {
  Container,
  Content,
  Description,
  Footer,
  GetToKnowMore,
  Heading,
  HeadingText,
  MyNewsButton,
  Name,
  SignUpButtonContainer,
  StyledIconClock,
  StyledSignUpButton,
  Summary,
} from "./styles";

export type Props = React.ButtonHTMLAttributes<HTMLDivElement> & {
  isSingleNewsletter?: boolean;
  reference: newsletterDetailsNewsletter$key;
};

export const NewsletterDetails: React.FunctionComponent<Props> = ({
  children,
  isSingleNewsletter = false,
  reference,
  ...props
}) => {
  const newsletter = useFragment(
    graphql`
      fragment newsletterDetailsNewsletter on Newsletter {
        name
        entityId
        newsletterCategories {
          name
        }
        summary {
          text
        }
        description {
          text
        }
        ...entityLink
        ...signUpButtonNewsletter
      }
    `,
    reference,
  );

  const handleClick = useCallback(() => {
    document
      .querySelector("#newsletterCategorySlideshow")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const description = useMemo(() => {
    if (!notEmpty(newsletter.description?.text)) return null;
    return <Description> {newsletter.description.text}</Description>;
  }, [newsletter.description]);

  const summary = useMemo(() => {
    if (!notEmpty(newsletter.summary?.text)) return null;
    return (
      <Summary>
        <StyledIconClock />
        {newsletter.summary.text}
      </Summary>
    );
  }, [newsletter.summary]);

  return (
    <Container {...props}>
      {children}
      <Content>
        <Heading>
          <HeadingText>
            <span>Sign up</span> for
          </HeadingText>
          <Name>{newsletter.name}</Name>
        </Heading>
        {description}
        {summary}
        <SignUpButtonContainer>
          {newsletter.entityId === newsletterData.myNews.entityId ? (
            <MyNewsButton anchorProps={{ "aria-label": "my news" }} as="/mynews" pathname="mynews">
              Set Up Now
            </MyNewsButton>
          ) : (
            <StyledSignUpButton reference={[newsletter]} />
          )}
        </SignUpButtonContainer>
      </Content>
      {!isSingleNewsletter && (
        <Footer>
          {"This newsletter is part of the "}
          <span>
            &lsquo;{first(newsletter.newsletterCategories)?.name}&rsquo; {" bundle. "}
          </span>
          <GetToKnowMore onClick={handleClick}>
            See what else is included in this bundle.
          </GetToKnowMore>
        </Footer>
      )}
    </Container>
  );
};

NewsletterDetails.displayName = "NewsletterDetails";
